import type { ReactNode } from 'react';

export interface ChipProps {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** Renders as a static label rather than a button. */
  readOnly?: boolean;
  title?: string;
}

/**
 * The pill used for categories, payers and who-is-included. Selected state is
 * a filled accent pill, matching the prototype.
 *
 * `aria-pressed` rather than a checkbox role: these are toggles that act
 * immediately, and a screen reader announcing "pressed" is closer to what
 * happens than "checked".
 */
export function Chip({ children, selected = false, onClick, readOnly = false, title }: ChipProps) {
  const style = {
    borderRadius: 'var(--r-pill)',
    padding: '9px 16px',
    fontSize: 'var(--fs-body-md)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
    background: selected ? 'var(--accent)' : 'var(--surface)',
    color: selected ? '#fff' : 'var(--text-secondary)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    cursor: readOnly ? 'default' : 'pointer',
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  };

  if (readOnly) {
    return (
      <span style={style} title={title}>
        {children}
      </span>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-pressed={selected} style={style} title={title}>
      {children}
    </button>
  );
}

/** A horizontally scrolling row of chips that doesn't show a scrollbar. */
export function ChipRow({ children }: { children: ReactNode }) {
  return (
    <div
      className="hide-scrollbar"
      style={{
        display: 'flex',
        gap: 'var(--sp-2)',
        overflowX: 'auto',
        paddingBottom: 2,
      }}
    >
      {children}
    </div>
  );
}

/** Chips that wrap onto multiple lines — payers, split membership. */
export function ChipWrap({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>{children}</div>
  );
}
